import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Minus, 
  ArrowDownLeft, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Info,
  Layers,
  Percent,
  Calculator,
  Lock,
  X
} from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';

interface StakingPageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  onStake: (asset: string, amount: number, duration: number) => Promise<boolean>;
  availableBalance?: number;
}

interface StakingAsset {
  id: string;
  name: string;
  symbol: string;
  iconUrl: string;
  flexibleApy: number;
  fixedApyByDuration: Record<number, number>;
  minStake: number;
  lockPeriods: number[]; // in days
  totalStaked: number;
  balance?: number;
  price?: number;
}

type StakingPlanType = 'flexible' | 'fixed';
type StakingView = 'flexible' | 'festgeld';

type FAQItem = {
  question: string;
  answer: string;
};

const StakingPage: React.FC<StakingPageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  onStake,
  availableBalance
}) => {
  const { t } = useTranslation();
  const { userStakes, addUserStake, addTransaction, calculateCurrentEarnings, unstakeFlexibleStake, claimUserStake, fetchUserStakes } = useDatabase();
  
  // State for staking form
  const [stakingAmount, setStakingAmount] = useState('');
  const [stakingDuration, setStakingDuration] = useState(30);
  const [selectedPlanType, setSelectedPlanType] = useState<StakingPlanType>('fixed');
  const [selectedAsset, setSelectedAsset] = useState<StakingAsset | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // State for staking operations
  const [isStaking, setIsStaking] = useState(false);
  const [stakingMessage, setStakingMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  
  // State for expanded asset details
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  
  // State for calculator
  const [calculatorAsset, setCalculatorAsset] = useState('btc-stake');
  const [calculatorPlanType, setCalculatorPlanType] = useState<StakingPlanType>('fixed');
  const [activeStakingView, setActiveStakingView] = useState<StakingView>('flexible');
  const [calculatorAmount, setCalculatorAmount] = useState('1');
  const [calculatorDuration, setCalculatorDuration] = useState('30');
  const [claimingStakes, setClaimingStakes] = useState<Record<string, boolean>>({});
  const [calculatorResults, setCalculatorResults] = useState({
    estimatedEarnings: 0,
    estimatedEarningsUsd: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
    yearly: 0
  });

  // Helper function to render crypto icon with fallback
  const renderCryptoIcon = (currency: { symbol: string, iconUrl: string }) => {
    return (
      <div className="relative w-10 h-10 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center">
        <img 
          src={currency.iconUrl} 
          alt={currency.symbol}
          className="w-full h-full object-contain"
          onError={(e) => {
            // On error, replace with text icon
            e.currentTarget.style.display = 'none';
            const fallbackEl = e.currentTarget.nextElementSibling as HTMLElement | null;
            if (fallbackEl) {
              fallbackEl.classList.remove('hidden');
              fallbackEl.style.display = 'flex';
            }
          }}
        />
        <div className="absolute inset-0 hidden items-center justify-center text-white font-bold">
          {currency.symbol.substring(0, 2)}
        </div>
      </div>
    );
  };

  // Available staking assets
  const [availableStakingAssets, setAvailableStakingAssets] = useState<StakingAsset[]>([
    {
      id: 'btc-stake',
      name: 'Bitcoin',
      symbol: 'BTC',
      iconUrl: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
      flexibleApy: 35.0,
      fixedApyByDuration: { 30: 70.0, 60: 70.0, 90: 70.0, 180: 70.0, 365: 70.0 },
      minStake: 0.001,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 1250.75,
      balance: btcBalance,
      price: currentBtcPrice
    },
    {
      id: 'usdt-stake',
      name: 'Tether',
      symbol: 'USDT',
      iconUrl: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
      flexibleApy: 34.0,
      fixedApyByDuration: { 30: 68.0, 60: 68.0, 90: 68.0, 180: 68.0, 365: 68.0 },
      minStake: 100,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 5750000,
      balance: availableBalance || 0,
      price: 1
    },
    {
      id: 'eth-stake',
      name: 'Ethereum',
      symbol: 'ETH',
      iconUrl: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
      flexibleApy: 36.0,
      fixedApyByDuration: { 30: 72.0, 60: 72.0, 90: 72.0, 180: 72.0, 365: 72.0 },
      minStake: 0.01,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 32500.5,
      balance: 0,
      price: 3500
    },
    {
      id: 'sol-stake',
      name: 'Solana',
      symbol: 'SOL',
      iconUrl: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
      flexibleApy: 35.0,
      fixedApyByDuration: { 30: 70.0, 60: 70.0, 90: 70.0, 180: 70.0, 365: 70.0 },
      minStake: 1,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 185000,
      balance: 0,
      price: 150
    }
  ]);

  const getFixedApy = useCallback((asset: StakingAsset, durationDays: number) => {
    return asset.fixedApyByDuration[durationDays] ?? asset.fixedApyByDuration[asset.lockPeriods[0]] ?? 0;
  }, []);

  const getApyByPlan = useCallback((asset: StakingAsset, planType: StakingPlanType, durationDays: number) => {
    return planType === 'flexible' ? asset.flexibleApy : getFixedApy(asset, durationDays);
  }, [getFixedApy]);

  // Fetch user stakes on mount
  useEffect(() => {
    fetchUserStakes();
  }, [fetchUserStakes]);

  useEffect(() => {
    if (activeStakingView === 'flexible') {
      setCalculatorPlanType('flexible');
    } else if (activeStakingView === 'festgeld') {
      setCalculatorPlanType('fixed');
    }
  }, [activeStakingView]);

  // Update asset balances when they change
  useEffect(() => {
    setAvailableStakingAssets(prev => 
      prev.map(asset => {
        if (asset.symbol === 'BTC') {
          return { ...asset, balance: btcBalance, price: currentBtcPrice };
        } else if (asset.symbol === 'USDT') {
          return { ...asset, balance: availableBalance || usdtBalance, price: 1 };
        }
        return asset;
      })
    );
  }, [btcBalance, usdtBalance, currentBtcPrice, availableBalance]);

  // Clear staking message after 5 seconds
  useEffect(() => {
    if (stakingMessage) {
      const timer = setTimeout(() => {
        setStakingMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [stakingMessage]);

  // Update calculator results when inputs change
  useEffect(() => {
    const updateCalculatorResults = () => {
      const asset = availableStakingAssets.find(a => a.id === calculatorAsset);
      if (!asset) return;

      const amount = parseFloat(calculatorAmount) || 0;
      const days = parseInt(calculatorDuration) || 30;
      const apy = getApyByPlan(asset, calculatorPlanType, days);

      // Calculate daily rate
      const dailyRate = apy / 365 / 100;
      
      // Calculate earnings
      const estimatedEarnings = amount * dailyRate * days;
      const estimatedEarningsUsd = estimatedEarnings * (asset.price || 0);
      
      // Calculate periodic earnings
      const daily = amount * dailyRate;
      const weekly = daily * 7;
      const monthly = daily * 30;
      const yearly = amount * (apy / 100);
      
      setCalculatorResults({
        estimatedEarnings,
        estimatedEarningsUsd,
        daily,
        weekly,
        monthly,
        yearly
      });
    };
    
    updateCalculatorResults();
  }, [calculatorAsset, calculatorAmount, calculatorDuration, calculatorPlanType, availableStakingAssets, getApyByPlan]);

  // Calculate estimated return based on amount, duration, and APY
  const calculateEstimatedReturn = (amount: number, durationDays: number, apy: number) => {
    // Convert APY to daily rate
    const dailyRate = apy / 365 / 100;
    
    // Calculate interest for the staking period
    const interest = amount * dailyRate * durationDays;
    
    return interest;
  };

  // Handle stake button click
  const handleStakeClick = (asset: StakingAsset) => {
    const resolvedPlanType = activeStakingView === 'flexible' ? 'flexible' : 'fixed';

    setSelectedAsset(asset);
    setSelectedPlanType(resolvedPlanType);
    setStakingAmount('');
    setStakingDuration(asset.lockPeriods[0]);
    setShowConfirmationModal(true);
  };

  const withStakeLoading = async (stakeId: string, action: () => Promise<boolean>) => {
    setClaimingStakes(prev => ({ ...prev, [stakeId]: true }));
    try {
      const success = await action();
      if (!success) {
        setStakingMessage({
          type: 'error',
          text: t('staking.stakingFailed')
        });
      }
    } finally {
      setClaimingStakes(prev => ({ ...prev, [stakeId]: false }));
    }
  };

  // Confirm stake
  const confirmStake = async () => {
    if (!selectedAsset) return;
    
    // Clear previous messages
    setStakingMessage(null);
    
    const amount = parseFloat(stakingAmount);
    
    // Validate input
    if (isNaN(amount) || amount <= 0) {
      setStakingMessage({
        type: 'error',
        text: t('staking.enterValidAmount')
      });
      return;
    }
    
    // Check minimum stake
    if (amount < selectedAsset.minStake) {
      setStakingMessage({
        type: 'error',
        text: t('staking.minimumStakeAmount', { amount: selectedAsset.minStake, asset: selectedAsset.symbol })
      });
      return;
    }
    
    // Check available balance
    const stakingValueUSD = selectedAsset.symbol === 'USDT' ? amount : amount * currentBtcPrice;
    
    if (availableBalance !== undefined && stakingValueUSD > availableBalance) {
      setStakingMessage({
        type: 'error',
        text: t('staking.insufficientAvailableBalance', { 
          required: stakingValueUSD.toFixed(2), 
          available: availableBalance.toFixed(2) 
        })
      });
      return;
    }
    
    // Check balance
    const actualBalance = selectedAsset.symbol === 'USDT' && availableBalance !== undefined 
      ? Math.min(selectedAsset.balance || 0, availableBalance) 
      : selectedAsset.balance;
    
    if (actualBalance && amount > actualBalance) {
      setStakingMessage({
        type: 'error',
        text: t('staking.insufficientBalance', { asset: selectedAsset.symbol })
      });
      return;
    }
    
    // Allow multiple stakes per asset
    
    setIsStaking(true);
    
    try {
      // Call the addUserStake function
      const selectedApy = getApyByPlan(selectedAsset, selectedPlanType, stakingDuration);
      const effectiveDuration = selectedPlanType === 'fixed' ? stakingDuration : 0;
      const success = await addUserStake(
        selectedAsset.symbol,
        amount,
        selectedApy,
        effectiveDuration,
        selectedPlanType
      );
      
      if (success) {
        try {
          await addTransaction({
            type: 'stake',
            amount: -amount,
            description: `${selectedPlanType === 'flexible' ? 'Flexible' : 'Fixed Staking'} stake: ${amount} ${selectedAsset.symbol}`,
            status: 'completed'
          });
        } catch (txError) {
          console.warn('Transaction logging error (non-blocking):', txError);
        }

        setStakingMessage({
          type: 'success',
          text: t('staking.stakingSuccessful', { 
            amount: amount, 
            asset: selectedAsset.symbol, 
            duration: stakingDuration 
          })
        });
        
        // Close modal and reset form after 2 seconds
        setTimeout(() => {
          setShowConfirmationModal(false);
          setStakingAmount('');
          setSelectedAsset(null);
          setStakingMessage(null);
        }, 2000);
        
        // Call the onStake prop to update balances
        try {
          await onStake(selectedAsset.symbol, amount, effectiveDuration);
        } catch (err) {
          console.warn('onStake callback error (non-blocking):', err);
        }
        
        // Refresh user stakes
        await fetchUserStakes();
      } else {
        throw new Error('Staking returned false - operation was rejected');
      }
    } catch (error: unknown) {
      console.error('Staking error:', error);
      setStakingMessage({
        type: 'error',
        text: error instanceof Error ? error.message : t('staking.stakingFailed')
      });
    } finally {
      setIsStaking(false);
    }
  };

  // Handle max button click
  const handleMaxClick = () => {
    if (selectedAsset) {
      const actualBalance = selectedAsset.symbol === 'USDT' ? (availableBalance || usdtBalance) : selectedAsset.balance;
      
      if (actualBalance) {
        setStakingAmount(actualBalance.toString());
      }
    }
  };

  // Toggle asset details expansion
  const toggleAssetDetails = (assetId: string) => {
    if (expandedAssetId === assetId) {
      setExpandedAssetId(null);
    } else {
      setExpandedAssetId(assetId);
    }
  };

  // Calculate time remaining for a stake
  const calculateTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return t('staking.completed');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h ${t('staking.remaining')}`;
  };

  // Calculate progress percentage for a stake
  const calculateProgress = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    if (elapsed <= 0) return 0;
    if (elapsed >= total) return 100;
    
    return (elapsed / total) * 100;
  };

  // Get active stakes
  const activeStakes = userStakes.filter(stake => stake.status === 'active');
  
  // Get completed stakes
  const completedStakes = userStakes.filter(stake => stake.status === 'completed');

  const calculatorSelectedAssetData = availableStakingAssets.find(a => a.id === calculatorAsset);
  const calculatorSelectedSymbol = calculatorSelectedAssetData?.symbol;

  const faqItems: FAQItem[] = activeStakingView === 'flexible'
    ? [
        {
          question: t('staking.flexibleFaq1Q', { defaultValue: 'What is flexible staking?' }),
          answer: t('staking.flexibleFaq1A', { defaultValue: 'Flexible staking lets you earn rewards continuously while keeping the option to unstake at any time.' })
        },
        {
          question: t('staking.flexibleFaq2Q', { defaultValue: 'When are rewards realized?' }),
          answer: t('staking.flexibleFaq2A', { defaultValue: 'Rewards accrue in real time and are realized when you unstake your position.' })
        },
        {
          question: t('staking.flexibleFaq3Q', { defaultValue: 'Is there a lock period?' }),
          answer: t('staking.flexibleFaq3A', { defaultValue: 'No. Flexible staking has no mandatory lock period.' })
        }
      ]
    : [
        {
          question: t('staking.festgeldFaq1Q', { defaultValue: 'What is fixed-term staking?' }),
          answer: t('staking.festgeldFaq1A', { defaultValue: 'Fixed-term staking locks your funds for a set period in exchange for higher guaranteed APY.' })
        },
        {
          question: t('staking.festgeldFaq2Q', { defaultValue: 'Can I withdraw before maturity?' }),
          answer: t('staking.festgeldFaq2A', { defaultValue: 'No. Fixed staking positions must be held until the selected maturity date.' })
        },
        {
          question: t('staking.festgeldFaq3Q', { defaultValue: 'How is APY set?' }),
          answer: t('staking.festgeldFaq3A', { defaultValue: 'Fixed staking APY is approximately 2x flexible APY, with yearly returns targeting around 70% (+/-).' })
        }
      ];
  return (
    <div className="min-h-screen p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent mb-2">
            {t('staking.title')}
          </h1>
        </div>
        <p className="text-slate-400">
          {t('staking.subtitle')}
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setActiveStakingView('flexible')}
            className={`px-4 py-2.5 rounded-xl border transition-all ${activeStakingView === 'flexible' ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-200' : 'bg-slate-900/30 border-slate-700/50 text-slate-300 hover:border-slate-500/50'}`}
          >
            {t('staking.flexible', { defaultValue: 'Flexible' })}
          </button>
          <button
            type="button"
            onClick={() => setActiveStakingView('festgeld')}
            className={`px-4 py-2.5 rounded-xl border transition-all ${activeStakingView === 'festgeld' ? 'bg-amber-500/20 border-amber-400/50 text-amber-200' : 'bg-slate-900/30 border-slate-700/50 text-slate-300 hover:border-slate-500/50'}`}
          >
            {t('staking.festgeld', { defaultValue: 'Fixed Staking' })}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {stakingMessage && (
        <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
          stakingMessage.type === 'success' 
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {stakingMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          {stakingMessage.text}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Available Staking Assets */}
        <div className="lg:col-span-2 space-y-8">
          {activeStakingView === 'flexible' && (
            <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 backdrop-blur-sm rounded-2xl p-6 border border-cyan-700/30 shadow-2xl">
              <h2 className="text-xl font-semibold text-cyan-100 mb-2">{t('staking.flexibleModeTitle', { defaultValue: 'Flexible Staking Mode' })}</h2>
              <p className="text-cyan-200/80 text-sm">
                {t('staking.flexibleModeDescription', { defaultValue: 'This mode focuses on liquidity: you can unstake anytime while earning rewards continuously.' })}
              </p>
            </div>
          )}

          {activeStakingView === 'festgeld' && (
            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 backdrop-blur-sm rounded-2xl p-6 border border-amber-700/30 shadow-2xl">
              <h2 className="text-xl font-semibold text-amber-100 mb-2">{t('staking.festgeldTitle', { defaultValue: 'Fixed-Term Staking' })}</h2>
              <p className="text-amber-200/80 text-sm leading-relaxed mb-3">
                {t('staking.festgeldDescription', { defaultValue: 'Fixed-term staking is a locked staking product similar to a time deposit: your funds are locked for the chosen period, and in return you receive a higher guaranteed APY.' })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-black/20 rounded-lg px-3 py-2 border border-amber-500/20">
                  <div className="text-amber-300 font-medium">{t('staking.festgeldRule1Title', { defaultValue: 'Higher APY' })}</div>
                  <div className="text-amber-100/80">{t('staking.festgeldRule1', { defaultValue: 'Fixed staking APY is set to 2x the flexible APY.' })}</div>
                </div>
                <div className="bg-black/20 rounded-lg px-3 py-2 border border-amber-500/20">
                  <div className="text-amber-300 font-medium">{t('staking.festgeldRule2Title', { defaultValue: 'Annual Target' })}</div>
                  <div className="text-amber-100/80">{t('staking.festgeldRule2', { defaultValue: 'Yearly fixed APY is designed around 70% (+/-).' })}</div>
                </div>
                <div className="bg-black/20 rounded-lg px-3 py-2 border border-amber-500/20">
                  <div className="text-amber-300 font-medium">{t('staking.festgeldRule3Title', { defaultValue: 'Locked Until Maturity' })}</div>
                  <div className="text-amber-100/80">{t('staking.festgeldRule3', { defaultValue: 'Funds remain locked until maturity, then become claimable.' })}</div>
                </div>
              </div>
            </div>
          )}

          {/* Available Staking Assets */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Layers size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">{t('staking.availableStakingAssets')}</h2>
              </div>
              <div className="text-sm text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
                {availableStakingAssets.length} {t('staking.assets')}
              </div>
            </div>
            
            <div className="space-y-4">
              {availableStakingAssets.map((asset) => {
                return (
                  <div key={asset.id} className="bg-slate-900/40 rounded-xl border border-slate-700/50 overflow-hidden">
                    {/* Asset Header */}
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {renderCryptoIcon(asset)}
                        <div>
                          <div className="font-semibold text-white">{asset.name}</div>
                          <div className="text-sm text-slate-400">{asset.symbol}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 md:gap-4 flex-1 justify-end">
                        <div className="text-right hidden sm:block">
                          <div className="text-xs md:text-sm text-slate-400">
                            {activeStakingView === 'flexible'
                              ? t('staking.flexible', { defaultValue: 'Flexible' })
                              : t('staking.festgeld', { defaultValue: 'Fixed Staking' })}
                          </div>
                          <div className="text-emerald-400 font-bold text-sm md:text-base">
                            {activeStakingView === 'flexible'
                              ? `${asset.flexibleApy}%`
                              : `${getFixedApy(asset, asset.lockPeriods[0])}%`}
                          </div>
                        </div>
                        
                        <div className="text-right mr-2">
                          <div className="text-xs md:text-sm text-slate-400">{t('common.balance')}</div>
                          <div className="text-white font-medium text-sm md:text-base">
                            {(asset.symbol === 'USDT' 
                              ? (availableBalance !== undefined ? availableBalance : usdtBalance)
                              : asset.balance || 0
                            ).toFixed(asset.symbol === 'USDT' ? 2 : 6)} {asset.symbol}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => toggleAssetDetails(asset.id)}
                          className="w-8 h-8 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                        >
                          {expandedAssetId === asset.id ? (
                            <Minus size={18} className="text-slate-400" />
                          ) : (
                            <Plus size={18} className="text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedAssetId === asset.id && (
                      <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-slate-400 mb-1">{t('common.min')} Stake</div>
                            <div className="text-white font-medium">{asset.minStake} {asset.symbol}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-400 mb-1">Total Staked</div>
                            <div className="text-white font-medium">{asset.totalStaked.toLocaleString()} {asset.symbol}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-400 mb-1">{t('common.price')}</div>
                            <div className="text-white font-medium">${(asset.price || 0).toLocaleString()}</div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-sm text-slate-400 mb-2">Available Lock Periods</div>
                          <div className="flex flex-wrap gap-2">
                            {asset.lockPeriods.map((period) => (
                              <div key={period} className="px-3 py-1 bg-slate-800 rounded-lg text-sm text-slate-300 border border-slate-700/50" translate="no">
                                {period} {t('staking.days')} ({getFixedApy(asset, period)}%)
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {activeStakingView === 'flexible' && (
                          <div className="flex justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => handleStakeClick(asset)} 
                              disabled={!asset.balance || asset.balance < asset.minStake}
                              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 md:px-6 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-cyan-500/25 flex items-center gap-2"
                            >
                              <Layers size={16} />
                              {`${t('staking.stake')} ${t('staking.flexible', { defaultValue: 'Flexible' })}`}
                            </button>
                          </div>
                        )}

                        {activeStakingView === 'festgeld' && (
                          <div className="flex justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => handleStakeClick(asset)} 
                              disabled={!asset.balance || asset.balance < asset.minStake}
                              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 md:px-6 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-amber-500/25 flex items-center gap-2"
                            >
                              <Lock size={16} />
                              {t('staking.stake')} {t('staking.festgeld', { defaultValue: 'Fixed Staking' })}
                            </button>
                          </div>
                        )}


                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Active Stakes */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <TrendingUp size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">{t('staking.yourActiveStakes')}</h2>
            </div>
            
            {activeStakes.length > 0 ? (
              <div className="space-y-4">
                {activeStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);
                  const stakeType = stake.stake_type || 'fixed';
                  const progress = calculateProgress(stake.start_date, stake.end_date);
                  const timeRemaining = calculateTimeRemaining(stake.end_date);
                  const currentEarnings = calculateCurrentEarnings(stake);
                  const isMatured = new Date(stake.end_date) <= new Date();
                  
                  return (
                    <div key={stake.id} className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {asset ? renderCryptoIcon(asset) : (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold">
                              {stake.asset_symbol.substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-white">{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs text-slate-400" translate="no">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-emerald-400 font-bold">{stake.apy_rate}% APY</div>
                          <div className={`text-[11px] inline-block px-2 py-0.5 rounded-md mt-1 ${stakeType === 'flexible' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-purple-500/20 text-purple-300'}`}>
                            {stakeType === 'flexible' ? t('staking.flexible', { defaultValue: 'Flexible' }) : t('staking.festgeld', { defaultValue: 'Festgeld' })}
                          </div>
                          <div className="text-xs text-slate-400">
                            {stakeType === 'fixed' ? `${new Date(stake.end_date).toLocaleDateString()} ${t('staking.endDate')}` : t('staking.unstakeAnytime', { defaultValue: 'Unstake anytime' })}
                          </div>
                        </div>
                      </div>
                      
                      {stakeType === 'fixed' && (
                        <>
                          {/* Progress Bar */}
                          <div className="w-full h-2 bg-slate-700 rounded-full mb-2">
                            <div 
                              className="h-2 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                          
                          <div className="flex justify-between text-xs text-slate-400 mb-3">
                            <div>{t('staking.started')}: {new Date(stake.start_date).toLocaleDateString()}</div>
                            <div>{t('staking.ends')}: {new Date(stake.end_date).toLocaleDateString()}</div>
                          </div>
                        </>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-slate-400">{t('staking.earnedSoFar')}</div>
                          <div className="text-emerald-400 font-medium" translate="no">{currentEarnings.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        {stakeType === 'flexible' ? (
                          <button
                            onClick={() => withStakeLoading(stake.id, async () => {
                              const success = await unstakeFlexibleStake(stake.id);
                              if (success) {
                                setStakingMessage({
                                  type: 'success',
                                  text: t('staking.unstakeSuccess', { defaultValue: 'Flexible stake unstaked successfully' })
                                });
                              }
                              return success;
                            })}
                            disabled={claimingStakes[stake.id]}
                            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 shadow-lg shadow-cyan-500/25 flex items-center gap-2"
                          >
                            {claimingStakes[stake.id] ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <>
                                <ArrowDownLeft size={16} />
                                {t('staking.unstake', { defaultValue: 'Unstake' })}
                              </>
                            )}
                          </button>
                        ) : isMatured ? (
                          <button
                            onClick={() => withStakeLoading(stake.id, async () => {
                              const success = await claimUserStake(stake.id);
                              if (success) {
                                setStakingMessage({
                                  type: 'success',
                                  text: t('staking.claimSuccess', { defaultValue: 'Stake claimed successfully' })
                                });
                              }
                              return success;
                            })}
                            disabled={claimingStakes[stake.id]}
                            className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 shadow-lg shadow-emerald-500/25 flex items-center gap-2"
                          >
                            {claimingStakes[stake.id] ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <>
                                <CheckCircle size={16} />
                                {t('staking.claim')}
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="text-sm">
                            <Clock size={14} className="text-slate-400 inline mr-1" />
                            <span className="text-slate-300">{timeRemaining}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Layers size={48} className="mx-auto mb-4 opacity-50" />
                <p className="mb-2">{t('staking.noActiveStakes')}</p>
                <p className="text-sm text-slate-600">{t('staking.startStaking')}</p>
              </div>
            )}
          </div>
          
          {/* Completed Stakes */}
          {completedStakes.length > 0 && (
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <CheckCircle size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">{t('staking.completedStakes')}</h2>
              </div>
              
              <div className="space-y-4">
                {completedStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);
                  
                  return (
                    <div key={stake.id} className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {asset ? renderCryptoIcon(asset) : (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold">
                              {stake.asset_symbol.substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-white">{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs text-slate-400" translate="no">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-emerald-400 font-bold">{stake.apy_rate}% APY</div>
                          <div className="text-xs text-slate-400">
                            {new Date(stake.start_date).toLocaleDateString()} - {new Date(stake.end_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-slate-400">{t('staking.totalEarned')}</div>
                          <div className="text-emerald-400 font-medium" translate="no">{stake.earned_amount.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        <div className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-lg text-xs font-medium">
                          {t('staking.completed')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Staking Benefits */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Info size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">{t('staking.stakingBenefits')}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25 mb-4">
                  <DollarSign size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('staking.passiveIncome')}</h3>
                <p className="text-slate-400 text-sm">{t('staking.whatIsStakingAnswer')}</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25 mb-4">
                  <Percent size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('staking.competitiveApy')}</h3>
                <p className="text-slate-400 text-sm">Get some of the highest yields in the market with our optimized staking pools.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25 mb-4">
                  <Lock size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('staking.flexibleTerms')}</h3>
                <p className="text-slate-400 text-sm">Choose from various lock periods to match your investment strategy.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Active Stakes & Calculator */}
        <div className="space-y-8">
          {/* Staking Calculator */}
          {activeStakingView === 'flexible' && (
            <div className="bg-gradient-to-br from-cyan-900/25 to-blue-900/25 backdrop-blur-sm rounded-2xl p-6 border border-cyan-700/30 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/25">
                  <Calculator size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">{t('staking.flexiblePlannerTitle', { defaultValue: 'Flexible Earnings Planner' })}</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('staking.selectAsset')}</label>
                  <select
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-cyan-600/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all custom-select"
                    value={calculatorAsset}
                    onChange={(e) => setCalculatorAsset(e.target.value)}
                  >
                    {availableStakingAssets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.name} ({asset.flexibleApy}% APY)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('common.amount')}</label>
                  <input
                    type="number"
                    placeholder={t('swap.enterAmount')}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-cyan-600/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                    value={calculatorAmount}
                    onChange={(e) => setCalculatorAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('staking.projectionHorizon', { defaultValue: 'Projection Horizon' })}</label>
                  <select
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-cyan-600/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all custom-select"
                    value={calculatorDuration}
                    onChange={(e) => setCalculatorDuration(e.target.value)}
                  >
                    <option value="7">7 {t('staking.days')}</option>
                    <option value="30">30 {t('staking.days')}</option>
                    <option value="90">90 {t('staking.days')}</option>
                    <option value="180">180 {t('staking.days')}</option>
                    <option value="365">365 {t('staking.days')}</option>
                  </select>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-cyan-600/30">
                  <div className="text-sm text-cyan-200 mb-1">{t('staking.estimatedEarnings')}</div>
                  <div className="text-2xl font-bold text-cyan-300" translate="no">{calculatorResults.estimatedEarnings.toFixed(6)} {calculatorSelectedSymbol}</div>
                  <div className="text-sm text-cyan-100/80" translate="no">≈ ${calculatorResults.estimatedEarningsUsd.toFixed(2)}</div>
                  <div className="mt-3 text-xs text-cyan-100/80">{t('staking.unstakeAnytime', { defaultValue: 'Unstake anytime' })}</div>
                </div>
              </div>
            </div>
          )}

          {activeStakingView === 'festgeld' && (
            <div className="bg-gradient-to-br from-amber-900/25 to-orange-900/25 backdrop-blur-sm rounded-2xl p-6 border border-amber-700/30 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/25">
                  <Calculator size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">{t('staking.festgeldCalculatorTitle', { defaultValue: 'Fixed Staking Maturity Calculator' })}</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('staking.selectAsset')}</label>
                  <select
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-amber-600/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all custom-select"
                    value={calculatorAsset}
                    onChange={(e) => setCalculatorAsset(e.target.value)}
                  >
                    {availableStakingAssets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.name} ({getFixedApy(asset, parseInt(calculatorDuration) || 30)}% APY)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('common.amount')}</label>
                  <input
                    type="number"
                    placeholder={t('swap.enterAmount')}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-amber-600/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
                    value={calculatorAmount}
                    onChange={(e) => setCalculatorAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('staking.lockPeriod')} ({t('staking.days')})</label>
                  <select
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-amber-600/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all custom-select"
                    value={calculatorDuration}
                    onChange={(e) => setCalculatorDuration(e.target.value)}
                  >
                    <option value="30">30 {t('staking.days')}</option>
                    <option value="60">60 {t('staking.days')}</option>
                    <option value="90">90 {t('staking.days')}</option>
                    <option value="180">180 {t('staking.days')}</option>
                    <option value="365">365 {t('staking.days')}</option>
                  </select>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-amber-600/30">
                  <div className="text-sm text-amber-200 mb-1">{t('staking.maturityValue', { defaultValue: 'Maturity Value' })}</div>
                  <div className="text-2xl font-bold text-amber-300" translate="no">
                    {((parseFloat(calculatorAmount) || 0) + calculatorResults.estimatedEarnings).toFixed(6)} {calculatorSelectedSymbol}
                  </div>
                  <div className="text-sm text-amber-100/80" translate="no">+ {calculatorResults.estimatedEarnings.toFixed(6)} {calculatorSelectedSymbol}</div>
                  <div className="mt-3 text-xs text-amber-100/80">{t('staking.festgeldRule2', { defaultValue: 'Yearly fixed APY is designed around 70% (+/-).' })}</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Staking FAQ */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Info size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                {activeStakingView === 'flexible'
                  ? t('staking.flexibleFaqTitle', { defaultValue: 'Flexible Staking FAQ' })
                  : t('staking.festgeldFaqTitle', { defaultValue: 'Fixed Staking FAQ' })}
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqItems.map((item, idx) => (
                <div key={`${activeStakingView}-${idx}`} className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                  <h3 className="font-medium text-white mb-2">{item.question}</h3>
                  <p className="text-sm text-slate-400">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Staking Confirmation Modal */}
      {showConfirmationModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('staking.confirmStake')}</h3>
              <button
                onClick={() => setShowConfirmationModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Error/Success Messages in Modal */}
            {stakingMessage && (
              <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
                stakingMessage.type === 'success' 
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}>
                {stakingMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                <span className="text-sm">{stakingMessage.text}</span>
              </div>
            )}
            
            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-4">
                  {renderCryptoIcon(selectedAsset)}
                  <div>
                    <div className="font-medium text-white">{selectedAsset.name}</div>
                    <div className="text-sm text-slate-400">
                      {getApyByPlan(selectedAsset, selectedPlanType, stakingDuration)}% APY ({selectedPlanType === 'flexible' ? t('staking.flexible', { defaultValue: 'Flexible' }) : t('staking.festgeld', { defaultValue: 'Fixed Staking' })})
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-slate-400 mb-2">{t('staking.planType', { defaultValue: 'Plan Type' })}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPlanType('flexible')}
                        disabled={activeStakingView === 'festgeld'}
                      className={`px-3 py-2 rounded-lg border transition-all ${selectedPlanType === 'flexible' ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-300' : 'bg-slate-900/40 border-slate-700/50 text-slate-300 hover:border-slate-500/50'}`}
                    >
                      {t('staking.flexible', { defaultValue: 'Flexible' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPlanType('fixed')}
                        disabled={activeStakingView === 'flexible'}
                      className={`px-3 py-2 rounded-lg border transition-all ${selectedPlanType === 'fixed' ? 'bg-purple-500/20 border-purple-400/50 text-purple-300' : 'bg-slate-900/40 border-slate-700/50 text-slate-300 hover:border-slate-500/50'}`}
                    >
                        {t('staking.festgeld', { defaultValue: 'Fixed Staking' })}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">{t('staking.amountToStake')}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={stakingAmount}
                        onChange={(e) => setStakingAmount(e.target.value)}
                        placeholder={`${t('common.min')} ${selectedAsset.minStake} ${selectedAsset.symbol}`}
                        className="w-full bg-slate-900/50 text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                        <button
                          onClick={handleMaxClick}
                          className="text-xs text-purple-400 hover:text-purple-300 transition-colors px-2 py-1 rounded bg-purple-400/10"
                        >
                          {t('common.max')}
                        </button>
                        <span className="text-slate-400">{selectedAsset.symbol}</span>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {t('common.available')}: {selectedAsset.symbol === 'USDT' 
                        ? (availableBalance !== undefined ? availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : usdtBalance.toFixed(2))
                        : selectedAsset.balance?.toFixed(selectedAsset.symbol === 'USDT' ? 2 : 6)
                      } {selectedAsset.symbol}
                    </div>
                  </div>
                  
                  {selectedPlanType === 'fixed' && (
                    <div>
                    <label className="block text-sm text-slate-400 mb-2">{t('staking.stakingPeriod')}</label>
                    <select
                      value={stakingDuration}
                      onChange={(e) => setStakingDuration(parseInt(e.target.value))}
                      className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all custom-select"
                    >
                      {selectedAsset.lockPeriods.map((period) => (
                        <option key={period} value={period}>{period} {t('staking.days')}</option>
                      ))}
                    </select>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Estimated Returns */}
              <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator size={16} className="text-purple-400" />
                  <span className="text-white font-medium">{t('staking.estimatedReturns')}</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{t('staking.apyRate')}</span>
                    <span className="text-emerald-400">{getApyByPlan(selectedAsset, selectedPlanType, stakingDuration)}%</span>
                  </div>
                  
                  {selectedPlanType === 'fixed' ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{t('staking.lockPeriod')}</span>
                      <span className="text-white" translate="no">{stakingDuration} {t('staking.days')}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{t('staking.lockPeriod')}</span>
                      <span className="text-white">{t('staking.noLock', { defaultValue: 'No lock period' })}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{t('staking.estimatedEarnings')}</span>
                    <span className="text-emerald-400" translate="no">
                      {calculateEstimatedReturn(
                        parseFloat(stakingAmount) || 0, 
                        selectedPlanType === 'fixed' ? stakingDuration : 30,
                        getApyByPlan(selectedAsset, selectedPlanType, stakingDuration)
                      ).toFixed(6)} {selectedAsset.symbol}
                    </span>
                  </div>
                  
                  {selectedPlanType === 'fixed' ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{t('staking.unlockDate')}</span>
                      <span className="text-white">
                        {new Date(Date.now() + stakingDuration * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{t('staking.unlockDate')}</span>
                      <span className="text-white">{t('staking.anytime', { defaultValue: 'Anytime' })}</span>
                    </div>
                  )}
                </div>
              </div>
              

              
              {/* Warning */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-400 flex items-start gap-3">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">{t('staking.importantInformation')}</p>
                  <p>
                    {selectedPlanType === 'fixed'
                      ? t('staking.stakingWarning')
                      : t('staking.flexibleWarning', { defaultValue: 'Flexible staking can be unstaked at any time. Rewards accrue continuously until you unstake.' })}
                  </p>
                </div>
              </div>
              
              {/* Action Button - Removed the cancel button and made the confirm button full width */}
              <button
                onClick={confirmStake}
                disabled={isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2"
              >
                {isStaking ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Layers size={18} />
                    {t('staking.confirmStake')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingPage;